import { ReactElement, useContext, useEffect, useMemo, useState, createContext, useCallback } from 'react';
import { AuthenticatedTemplate, UnauthenticatedTemplate, MsalProvider } from '@azure/msal-react';
import { AccountInfo, IPublicClientApplication, LogLevel, PublicClientApplication } from '@azure/msal-browser';
import track from 'c9-tracking';

import { type TConfig, useConfigContext } from 'context/ConfigContext';

/* ============================================ Local Functions ============================================ */
// ============================================ User
const useUserDetails: TUseUserDetails = () => {
  const [userDetails, setUserDetails] = useState<TUserDetails>({} as TUserDetails);

  const updateUserDetails: TUser['updateUserDetails'] = useCallback((newUserDetails: TUserDetails) => {
    setUserDetails((prevUserDetails) => {
      const updatedUserDetails = { ...prevUserDetails, ...newUserDetails };
      sessionStorage.setItem('userDetails', JSON.stringify(updatedUserDetails));
      return updatedUserDetails;
    });
  }, []);

  useEffect(() => {
    const userDetailsCache = sessionStorage.getItem('userDetails');

    if (userDetailsCache) {
      const userDetails: TUserDetails = JSON.parse(userDetailsCache);
      setUserDetails(userDetails);
    }
  }, []);

  return { userDetails, updateUserDetails };
};

// ============================================ MSAL Authentication
const useMSALDetails: TUseMSALDetails = (msalDetails, userEmail, updateUserDetails) => {
  const msalConfig = useMemo(() => {
    return {
      auth: {
        clientId: msalDetails.clientId,
        authority: `https://login.microsoftonline.com/${msalDetails.authorityId}`,
        redirect_uri: msalDetails.redirectUri
      },
      cache: {
        cacheLocation: 'sessionStorage',
        storeAuthStateInCookie: false
      },
      system: {
        loggerOptions: {
          loggerCallback: (level: LogLevel, message: string, containsPii: boolean): void => {
            if (containsPii) {
              return;
            }
            switch (level) {
              case LogLevel.Error:
                console.error(message);
                return;
              case LogLevel.Info:
                console.info(message);
                return;
              case LogLevel.Verbose:
                console.debug(message);
                return;
              case LogLevel.Warning:
                console.warn(message);
                return;
            }
          },
          piiLoggingEnabled: false
        },
        windowHashTimeout: 60000,
        iframeHashTimeout: 6000,
        loadFrameTimeout: 0,
        asyncPopups: false
      }
    };
  }, [msalDetails]);

  const msalInstance = useMemo(() => new PublicClientApplication(msalConfig), [msalConfig]);

  const handleOnLogin = useCallback(async () => {
    msalInstance!.loginRedirect({ scopes: ['User.Read'] });
  }, [msalInstance]);

  const handleOnLogout = useCallback(() => {
    msalInstance!.logoutRedirect({
      postLogoutRedirectUri: '/'
    });

    track.interact(userEmail + ' logged out');
  }, [msalInstance, userEmail]);

  useEffect(() => {
    if (!msalInstance) {
      return;
    }

    const userDetail: AccountInfo = msalInstance.getAllAccounts()[0];

    if (!!userDetail) {
      const loggedInUser = {
        name: userDetail.name || '',
        email: userDetail.username || ''
      };

      updateUserDetails(loggedInUser);
      track.info(`${loggedInUser.email} successfully logged in.`);
    }
  }, [msalInstance, updateUserDetails]);

  return { msalInstance, onLogin: handleOnLogin, onLogout: handleOnLogout };
};

/* ============================================ Main Components ============================================ */
const AppContext = createContext<TAppContext>({} as TAppContext);

export const AppContextProvider: TAppContextProvider = ({ children }) => {
  const { msal: msalDetails } = useConfigContext();

  const user = useUserDetails();
  const msal = useMSALDetails(msalDetails, user.userDetails.email, user.updateUserDetails);

  return (
    <AppContext.Provider value={{ user, msal }}>
      <MsalProvider instance={msal.msalInstance}>{children}</MsalProvider>
    </AppContext.Provider>
  );
};

export const useAppContext = () => useContext(AppContext);
export { AuthenticatedTemplate, UnauthenticatedTemplate };

/* ============================================ Local Types ============================================ */
// ============================================ User
type TUserDetails = {
  name: string;
  email: string;
};

type TUser = {
  userDetails: TUserDetails;
  updateUserDetails: (userDetail: TUserDetails) => void;
};

type TUseUserDetails = () => TUser;

// ============================================ MSAL
type TMSAL = {
  msalInstance: IPublicClientApplication;
  onLogin: () => void;
  onLogout: () => void;
};

type TUseMSALDetails = (
  config: TConfig['msal'],
  userEmail: string,
  updateUserDetails: TUser['updateUserDetails']
) => TMSAL;

// ============================================ Main
type TAppContextProvider = (props: { children: ReactElement }) => ReactElement;

export type TAppContext = {
  user: TUser;
  msal: TMSAL;
};
